import { User, Clock, Search } from 'lucide-react'
import { Conversation } from '@/types'
import { useState, useMemo, useEffect } from 'react'
import { api } from '@/utils/api'
import { useChat } from '@/hooks/useChat'

interface ConversationListProps {
  conversations: Conversation[]
  selectedId?: string
  onSelect: (conversation: Conversation) => void
}

export default function ConversationList({ conversations, selectedId, onSelect }: ConversationListProps) {
  const { profiles, setProfile } = useChat()
  const [query, setQuery] = useState('')
  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60)
    
    if (diffInHours < 1) {
      return 'Just now'
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`
    } else {
      return date.toLocaleDateString()
    }
  }

  const getUnreadCount = (conversation: Conversation) => {
    return conversation.unreadCount || 0
  }

  const grouped = useMemo(() => {
    const byUser: Record<string, Conversation[]> = {}
    conversations.forEach(c => { const key = c.userId || 'anonymous'; (byUser[key] ||= []).push(c) })
    const threads = Object.entries(byUser).map(([userId, list]) => {
      const sorted = list.sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      return { userId, latest: sorted[0], count: list.length }
    })
    return threads
      .filter(t => {
        const profile = profiles[t.userId]
        const text = (profile?.fullName || '') + ' ' + (profile?.email || '') + ' ' + t.userId
        return text.toLowerCase().includes(query.toLowerCase())
      })
      .sort((a,b) => new Date(b.latest.createdAt).getTime() - new Date(a.latest.createdAt).getTime())
  }, [conversations, profiles, query])

  useEffect(() => {
    const uniqueUserIds = Array.from(new Set(conversations.map(c => c.userId).filter(Boolean) as string[]))
    uniqueUserIds.forEach(async (uid) => {
      if (!profiles[uid]) {
        const p = await api.getUserProfile(uid)
        if (p) setProfile(uid, p)
      }
    })
  }, [conversations])

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-3 border-b border-gray-200 flex items-center gap-2">
        <Search className="h-4 w-4 text-gray-400" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name or email" className="flex-1 border rounded px-2 py-2 text-base" />
      </div>
      {conversations.length === 0 ? (
        <div className="p-4 text-center text-gray-500">
          <User className="mx-auto h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">No conversations yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-200">
          {grouped.map(({ userId, latest }) => {
            const isSelected = latest.id === selectedId
            const unreadCount = getUnreadCount(latest)
            const profile = profiles[userId]
            
            return (
              <div
                key={latest.id}
                onClick={() => onSelect(latest)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-6 w-6 text-gray-500" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 py-0.5">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-base font-bold text-gray-900 truncate">
                          {profile?.fullName || profile?.email || (userId === 'anonymous' ? 'Anonymous User' : userId)}
                        </p>
                        <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-tighter">
                            {formatTime(latest.createdAt)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 truncate pr-4 font-medium">
                          {latest.lastMessage?.content}
                        </p>
                        {unreadCount > 0 && (
                          <span className="flex-shrink-0 bg-primary-600 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )})}
        </div>
      )}
    </div>
  )
}
